import Component from '@glimmer/component'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { includes } from 'teacher-ui-client/helpers/includes'

type ID = string | number

export interface OptionSet<OptionId> {
  id: string // the option set id must be a string
  legend?: string
  options: {
    id: OptionId
    label: string
  }[]
}

interface Args<OptionId> {
  selectAllLabel?: string
  checkedOptionIds: OptionId[]
  optionSet: OptionSet<OptionId>
  onSelectionChange: (optionSet: OptionSet<OptionId>, selectedIds: OptionId[]) => unknown
}

interface Signature<OptionId extends ID> {
  Element: HTMLFieldSetElement
  Args: Args<OptionId>
}

export class CheckboxGroup<OptionId extends ID> extends Component<Signature<OptionId>> {
  get allOptionsChecked() {
    const { checkedOptionIds, optionSet } = this.args
    const isOptionChecked = ({ id }: { id: OptionId }) => Boolean(checkedOptionIds?.includes?.(id))
    return Boolean(optionSet?.options?.every?.(isOptionChecked))
  }

  get availableOptionIds() {
    return this.args.optionSet.options.map((option) => option.id) || []
  }

  @action
  onSelectAll() {
    if (this.allOptionsChecked) {
      this.args.onSelectionChange(this.args.optionSet, [])
    } else {
      this.args.onSelectionChange(this.args.optionSet, this.availableOptionIds)
    }
  }

  @action
  onSelectOption({ id }: { id: OptionId }) {
    if (this.args.checkedOptionIds.includes(id)) {
      this.args.onSelectionChange(
        this.args.optionSet,
        this.args.checkedOptionIds.filter((optionId) => optionId !== id),
      )
    } else {
      this.args.onSelectionChange(this.args.optionSet, [id, ...this.args.checkedOptionIds])
    }
  }
  <template>
    <fieldset ...attributes>
      <legend>
        {{@optionSet.legend}}
      </legend>
      <ul>
        {{#if @selectAllLabel}}
          <li>
            <label class="inline-flex cursor-pointer items-center gap-1 font-bold select-none">
              <input
                type="checkbox"
                class="cursor-pointer"
                id="{{@optionSet.id}}.*"
                name="{{@optionSet.id}}"
                value="{{@optionSet.id}}.*"
                checked={{this.allOptionsChecked}}
                {{on "input" (fn this.onSelectAll @optionSet)}}
              />
              <span>
                {{@selectAllLabel}}
              </span>
            </label>
          </li>
        {{/if}}
        {{#each @optionSet.options as |option|}}
          <li>
            <label class="inline-flex cursor-pointer items-center gap-1 font-normal select-none">
              <input
                type="checkbox"
                class="cursor-pointer"
                id="{{@optionSet.id}}.{{option.id}}"
                name="{{@optionSet.id}}"
                value="{{option.id}}"
                checked={{includes @checkedOptionIds option.id}}
                {{on "input" (fn this.onSelectOption option)}}
              />
              <span>{{option.label}}</span>
            </label>
          </li>
        {{/each}}
      </ul>
    </fieldset>
  </template>
}

export default CheckboxGroup
